import { FilterBar } from "@repo/ui/Filters/filterBar";
import { useContext, type ReactNode } from "react";
import { AddFilterButton, checkHasAddableFilters } from "./addFilterButton.js";
import { Filter } from "./filter.js";
import { FilterContext } from "./filterContext.js";
import { checkIsFilterEqual } from "./filterEquality.js";
import type { FilterParams } from "./filterParams.js";
import { FilterResultSummary } from "./filterResultSummary.js";
import type { FilterKey, FilterValues } from "./filterState.js";
import { stripPlaybookHasResultConditions } from "./playbookCondition.js";

export interface RenderFiltersOptions {
  isLoading?: boolean;
  denominatorCount?: number | undefined;
  count?: number | undefined;
  /** Contextual controls before the filter items (e.g. a playbook switcher or search input). */
  leadingItems?: ReactNode;
  /** Filter-like controls appended after the filter items (e.g. a page-specific period selector). */
  trailingItems?: ReactNode;
  /** Controls on the right edge of the bar (e.g. a display columns selector). */
  rightActions?: ReactNode;
}

type UseFilterResult<FS extends readonly FilterKey[]> = [
  values: Pick<FilterValues, FS[number]>,
  renderFilters: (options?: RenderFiltersOptions) => ReactNode,
  /**
   * Whether the user has narrowed the results relative to this page's
   * defaults, ignoring "Include calls without grades". Pages that show a
   * "N of M" count gate the M query on this: the checkbox picks which calls
   * the page is about rather than selecting within them, so it moves both
   * numbers together and must not by itself produce a denominator.
   */
  hasNarrowingChanges: boolean,
];

/** Returns the values and a function to render a filter bar for a set of filters identified by the given keys. */
export function useFilters<FS extends readonly FilterKey[]>(
  filters: FS,
  params: FilterParams = {},
): UseFilterResult<FS> {
  const { defaultValues, values, onReset } = useContext(FilterContext);

  const hasChanges = filters.some(
    (filter) =>
      !checkIsFilterEqual(filter, values[filter], defaultValues[filter]),
  );

  // Reset restores the has-result default too, so it keys off `hasChanges`
  // above; only the denominator gate takes the condition out of the picture.
  const hasNarrowingChanges = filters.some((filter) =>
    filter === "playbookConditions"
      ? !checkIsFilterEqual(
          filter,
          stripPlaybookHasResultConditions(values.playbookConditions),
          stripPlaybookHasResultConditions(defaultValues.playbookConditions),
        )
      : !checkIsFilterEqual(filter, values[filter], defaultValues[filter]),
  );

  return [
    values,
    (options = {}) => (
      <FilterBar
        isLoading={options.isLoading}
        leadingItems={options.leadingItems}
        trailingItems={options.trailingItems}
        addFilterButton={
          // Only pass the element when it will render something — the bar
          // reserves a gap slot for a non-null `addFilterButton` node.
          checkHasAddableFilters(filters) ? (
            <AddFilterButton filters={filters} params={params} />
          ) : undefined
        }
        items={filters.map((filter) => (
          <Filter
            key={filter}
            filter={filter}
            params={params}
            filterKeys={filters}
          />
        ))}
        resultSummary={
          options.count !== undefined ? (
            <FilterResultSummary
              count={options.count}
              denominatorCount={options.denominatorCount}
            />
          ) : undefined
        }
        onReset={hasChanges ? onReset : undefined}
        rightActions={options.rightActions}
      />
    ),
    hasNarrowingChanges,
  ];
}
